<template>
  <div>

    <!-- Main Form -->
    <b-row>
      <b-col cols="12">
        <h3
          class="pt-3"
        >
          <strong>Message Delivery</strong>
        </h3>
      </b-col>
      <b-col cols="12">
        <advanced-form
          autocomplete="off"
          :hideFormControl="true"
          :form="formFields"
          v-bind="form"
          v-model="form"
        >

          <b-alert
            v-if="!Array.isArray(syncedEmailAddresses) || !syncedEmailAddresses.length"
            slot="from-before"
            variant="warning"
            show
          >
            It seems like no email addresses have been connected to Real Links yet. In order to send slack messages on behalf
            of you, you need to go to <a :href="$router.resolve({ name: 'EmailSetup' }).href" target="_blank">Settings > Connect Email</a> page.
          </b-alert>
        </advanced-form>
      </b-col>
    </b-row>

    <!-- Input Form with preview -->
    <b-row>
      <b-col cols="12">
        <h3
          class="pt-3"
        >
          <strong>Message Content</strong>
        </h3>
      </b-col>
      <b-col cols="12" lg="7">
        <div class="mb-3">Please note that the display below is just an estimated layout. Use the "Send Test" button to get an accurate preview.</div>
        <content-preview
          class="mb-3"
          :fromEmail="form.from"
          :job="inputForm.job || null"
          :contentShare="inputForm.contentShare || null"
          :collectiveGoal="collectiveGoal"
          :inputs="inputForm"
          :body="body"
          @sendTestMessage="$bvModal.show('send-test-message-modal')"
        />
      </b-col>
      <b-col cols="12" lg="5">
        <advanced-form
          autocomplete="off"
          :hideFormControl="true"
          :form="inputFormFields"
          v-bind="inputForm"
          v-model="inputForm"
        />
      </b-col>
    </b-row>

    <!-- Variables that can be used -->
    <b-row>
      <b-col>
        <b-card no-body class="mb-1">
          <b-card-header class="d-flex justify-content-between align-items-center bg-white">
            <div @click="displayVariables = !displayVariables" v-b-toggle.available-variables-card>
              <strong>Template variables you can use</strong>
            </div>
            <b-button @click="displayVariables = !displayVariables"  v-b-toggle.available-variables-card variant="outline-primary" class="px-2" size="sm">
              <font-awesome-icon v-if="displayVariables" icon="chevron-up" size="sm"/>
              <font-awesome-icon v-else icon="chevron-down" size="sm"/>
            </b-button>
          </b-card-header>
          <b-collapse id="available-variables-card" accordion="available-variables-card" role="tabpanel" class="p-3">
            <b>User related variables</b> you can use:<br>
            <span v-for="{ key, description } in variables.filter(({ type }) => type === 'USER')" :key="key" v-html="`{{${key}}} - ${description}<br>`"></span><br>
            <b>Job related variables</b> you can use:<br>
            <span v-for="{ key, description } in variables.filter(({ type }) => type === 'JOB')" :key="key" v-html="`{{${key}}} - ${description}<br>`"></span><br>
            <b>Leaderboard related variables</b> you can use:<br>
            <span v-for="{ key, description } in variables.filter(({ type }) => type === 'LEADERBOARD')" :key="key" v-html="`{{${key}}} - ${description}<br>`"></span><br>
          </b-collapse>
        </b-card>
      </b-col>
    </b-row>

    <b-row>
      <b-col class="pt-3">
        <b-button
          variant="primary"
          @click="input"
          class="float-right"
        >
          Save
        </b-button>
      </b-col>
    </b-row>

    <!-- Test Email Form -->
    <b-modal id="send-test-message-modal" title="Send Test Message" :hide-footer="true">
      <b-row>
        <b-col>
          <b-form>
            <!-- Email Addresses Field -->
            <test-message-form
              channel="email"
              :body="body"
              :subject="form.subject"
              :from="form.from"
              :job="inputForm.job"
              :contentShare="inputForm.contentShare"
              :collectiveGoal="collectiveGoal"
              :isIndividual="isIndividual"
            />
          </b-form>
        </b-col>
      </b-row>
    </b-modal>

    <!-- ALERT MODALS -->
    <b-modal id="target-group-change" title="Target group has changed" ok-only ok-title="I Understand">
      <div v-if="isIndividual">
        <p>This email will be sent as a direct message to everyone in the selected group.</p>
      </div>
      <div v-else>
        <p>This email will be sent as a group message to everyone in the selected group.</p>
      </div>
    </b-modal>
  </div>
</template>

<script>
import AdvancedForm from '@src/components/AdvancedForm';
import emailTemplates from '@src/views/campaign-admin/templates/emails';
import Share from '@src/components/Share';
import gridMixin from '@src/mixins/grid';
import ContentPreview from '@src/components/campaign-admin/message-previews/emails/content';
import TestMessageForm from '@src/components/campaign-admin/test-message-form.vue';
import { mapRoot } from '@src/mixins/root';

export default {
  props: {
    campaignID: {
      type: String,
    },
    collectiveGoal: {
      type: Object,
    },
    value: {
      type: Object,
    },
    syncedEmailAddresses: {
      type: Array,
    }
  },
  components: {
    AdvancedForm,
    Share,
    TestMessageForm,
    ContentPreview,
  },
  data() {
    const { emailMessage } = this.value || {};
    console.log({ emailMessage });
    delete emailMessage.__typename;
    const { template, inputs, body, job, contentShare, subject, ...rest } = emailMessage || {};
    return {
      form: {
        from: null,
        to: 'ALL_USERS',
        sendAt: '',
        status: 'DRAFT',
        subject: subject || '',
        ...(rest || {})
      },
      templateID: template || null,
      inputForm: {
        ...Object.keys(inputs || {}).reduce((acc, key) => {
          if (key.includes('IMAGE')) {
            try {
              acc[key] = JSON.parse(inputs[key]);
            } catch (e) {
              console.log(e);
              acc[key] = inputs[key];
            }
          } else {
            acc[key] = inputs[key];
          }
          return acc;
        }, {}),
        ...(job ? { job } : {}),
        ...(contentShare ? { contentShare } : {})
      },
      displayVariables: false,
    };
  },
  mixins: [
    gridMixin,
  ],
  watch: {
    isIndividual() {
      this.$bvModal.show('target-group-change');
    }
  },
  computed: {
    ...mapRoot([ 'isInMobi' ]),
    template() {
      const { templateID, isInMobi } = this;

      let templateFolder = null;
      if (isInMobi) {
        templateFolder = 'inMobi';
      }

      return emailTemplates(templateFolder).find(({ id }) => id === templateID);
    },
    toChoices() {
      const { isIndividual } = this;
      return {
        ALL_USERS: `All Users (Participants & Invited Users) - ${isIndividual ? 'as a direct email' : 'in a group'}`,
        REGISTERED_USERS: `Participants (Users who already joined the campaign) - ${isIndividual ? 'as a direct email' : 'in a group'}`,
        INVITED_USERS: `Invited Users (Users who have not joined the campaign yet) - ${isIndividual ? 'as a direct email' : 'in a group'}`,
      };
    },
    formFields() {
      const { syncedEmailAddresses, campaignID } = this;
      console.log({ campaignID });
      const defaultEmailOption = { NONE: '---- Select an email address ----' };
      return {
        from: {
          type: 'SELECT',
          label: 'From',
          disabled: !Array.isArray(syncedEmailAddresses) || !syncedEmailAddresses.length,
          choices: Array.isArray(syncedEmailAddresses) && syncedEmailAddresses.length
            ? syncedEmailAddresses.reduce((out, { emailAddress, fullName }) => {
              out[emailAddress] = `${emailAddress} (${fullName})`;
              return out;
            }, defaultEmailOption) : [ defaultEmailOption ],
          helpText: 'The person this message will be delieverd on behalf of'
        },
        to: {
          label: 'To',
          type: 'SELECT',
          required: true,
          choices: this.toChoices,
          helpText: 'The target group of this teams message'
        },
        subject: {
          label: 'Subject',
          type: 'STRING',
          required: true,
          helpText: 'Subject of this email'
        },
        sendAt: {
          label: 'Send At',
          placeholder: 'e.g. 2020-12-01 13:00',
          helpText: 'The time when this email will be sent, in your timezone',
          type: 'DATETIME',
        },
        status: {
          label: 'Status',
          type: 'SELECT',
          required: true,
          choices: {
            READY: 'Ready',
            DRAFT: 'Draft',
          },
        },
      };
    },
    inputFormFields() {
      const { template } = this;
      console.log({ template });
      const inputFields = template ? template.inputs : {};
      return {
        ...(this.isJob ? {
          job: {
            label: 'Job',
            type: 'RELATION',
            required: true,
            options: {
              type: 'jobs',
              title: 'Jobs',
              where: {
                status: 'ACTIVE',
              },
              many: false,
              order: 'CREATED_DESC',
            },
            helpText: 'Select the job you want to include in the teams message',
          },
        } : {}),
        ...(this.isContent ? {
          contentShare: {
            label: 'Content',
            type: 'RELATION',
            required: true,
            options: {
              type: 'contentshares',
              title: 'Content',
              many: false,
              where: {
                status: 'ACTIVE',
              },
              order: 'CREATED_DESC',
            },
            helpText: 'Select the piece of content you want to include in the teams message',
          },
        } : {}),
        ...inputFields,
      };
    },
    body() {
      const { template, inputForm } = this;
      if (!template) return null;
      const { body } = template;
      if (body && typeof body === 'string') {
        let replacedBody = body;
        Object.keys(inputForm).forEach(key => {
          let value = inputForm[key];
          if (value && typeof value === 'object') {
            value = value.fileLocation;
          }
          replacedBody = replacedBody.replaceAll(`[[${key}]]`, value);
        });
        return replacedBody;
      };
      let replacedBody = JSON.stringify(body);
      Object.keys(inputForm).forEach(key => {
        // console.log({ key, value: inputForm[key], tunrDown: turndownService.turndown(inputForm[key]).replaceAll('\\', '') });
        replacedBody = replacedBody.replaceAll(`[[${key}]]`);
      });
      console.log({ replacedBody });
      return replacedBody;
    },
    usedVariables() {
      const { body } = this;
      const stringBody = typeof body === 'string' ? body : (body ? JSON.stringify(body) : null);
      return [ ...new Set((stringBody || '').split('{{').reduce((r, el) => {
        if (!el.includes('}}')) return r;
        const [ variable ] = el.split('}}');
        r.push(variable);
        return r;
      }, [])) ];
    },
    variables() {
      return [
        {
          key: 'JOB_TITLE',
          type: 'JOB',
          target: 'INDIVIDUAL',
          description: 'Title of the selected job.',
        }, {
          key: 'JOB_SHARE_LINK',
          type: 'JOB',
          target: 'INDIVIDUAL',
          description: 'The link of the job users can share. This is uniquelly generated for every user.',
        }, {
          key: 'JOB_DESCRIPTION',
          type: 'JOB',
          target: 'INDIVIDUAL',
          description: 'Short description of the selected job.',
        }, {
          key: 'JOB_SHARE_TEXT',
          type: 'JOB',
          target: 'INDIVIDUAL',
          description: 'Suggested share text of the job.',
        }, {
          key: 'JOB_MATCH_LINK',
          type: 'JOB',
          target: 'INDIVIDUAL',
          description: 'LinkedIn network page of the user with possible matches',
        }, {
          key: 'CONTENT_TITLE',
          type: 'CONTENT',
          target: 'INDIVIDUAL',
          description: 'Title of the selected piece of content.',
        }, {
          key: 'CONTENT_SHARE_LINK',
          type: 'CONTENT',
          target: 'INDIVIDUAL',
          description: 'The link of the piece of content users can share. This is uniquelly generated for every user.',
        }, {
          key: 'CONTENT_DESCRIPTION',
          type: 'CONTENT',
          target: 'INDIVIDUAL',
          description: 'Short description of the selected piece of content.',
        }, {
          key: 'CONTENT_SHARE_TEXT',
          type: 'CONTENT',
          target: 'INDIVIDUAL',
          description: 'Suggested share text of the piece of content.',
        }, {
          key: 'LEADERBOARD_FIRST_POINTS',
          type: 'LEADERBOARD',
          target: 'GROUP',
          description: 'Amount of points/tickets collected by the participant being first on the leaderboard.',
        }, {
          key: 'LEADERBOARD_FIRST_NAME',
          type: 'LEADERBOARD',
          target: 'GROUP',
          description: 'The full name of the participant being first on the leaderboard.',
        }, {
          key: 'LEADERBOARD_SECOND_POINTS',
          type: 'LEADERBOARD',
          target: 'GROUP',
          description: 'Amount of points/tickets collected by the participant being second on the leaderboard.',
        }, {
          key: 'LEADERBOARD_SECOND_NAME',
          type: 'LEADERBOARD',
          target: 'GROUP',
          description: 'The full name of the participant being second on the leaderboard.',
        }, {
          key: 'LEADERBOARD_THIRD_POINTS',
          type: 'LEADERBOARD',
          target: 'GROUP',
          description: 'Amount of points/tickets collected by the participant being third on the leaderboard.',
        }, {
          key: 'LEADERBOARD_THIRD_NAME',
          type: 'LEADERBOARD',
          target: 'GROUP',
          description: 'The full name of the participant being third on the leaderboard.',
        }, {
          key: 'LEADERBOARD_FIRST_PRIZE_TITLE',
          type: 'LEADERBOARD',
          target: 'GROUP',
          description: 'The title of the first prize of the leaderboard.',
        }, {
          key: 'LEADERBOARD_SECOND_PRIZE_TITLE',
          type: 'LEADERBOARD',
          target: 'GROUP',
          description: 'The title of the second prize of the leaderboard.',
        }, {
          key: 'LEADERBOARD_THIRD_PRIZE_TITLE',
          type: 'LEADERBOARD',
          target: 'GROUP',
          description: 'The title of the third prize of the leaderboard.',
        }, {
          key: 'FIRST_NAME',
          type: 'USER',
          target: 'INDIVIDUAL',
          description: 'The first name of the current user on the leaderboard.',
        }, {
          key: 'LAST_NAME',
          type: 'USER',
          target: 'INDIVIDUAL',
          description: 'The last name of the current user on the leaderboard.',
        }, {
          key: 'LEADERBOARD_POSITION',
          type: 'USER',
          target: 'INDIVIDUAL',
          description: 'The position of the current user on the leaderboard.',
        }, {
          key: 'LEADERBOARD_POINTS',
          type: 'USER',
          target: 'INDIVIDUAL',
          description: 'The amount of points/tokens the current user has collected.',
        },
      ];
    },
    isIndividual() {
      const keys = this.variables.filter(({ target }) => target === 'INDIVIDUAL').map(({ key }) => key);
      return !!keys.filter(key => this.usedVariables.includes(key)).length;
    },
    isJob() {
      const keys = this.variables.filter(({ type }) => type === 'JOB').map(({ key }) => key);
      const { relations } = this.template || {};
      return (Array.isArray(relations) && relations.includes('JOB')) || !!keys.filter(key => this.usedVariables.includes(key)).length;
    },
    isContent() {
      const keys = this.variables.filter(({ type }) => type === 'CONTENT').map(({ key }) => key);
      const { relations } = this.template || {};
      return (Array.isArray(relations) && relations.includes('CONTENT')) || !!keys.filter(key => this.usedVariables.includes(key)).length;
    }
  },
  methods: {
    input() {
      const { form, inputForm } = this;
      if (inputForm.job) {
        delete inputForm.job.customFields;
        delete inputForm.job.location;
        delete inputForm.job.createdAt;
        delete inputForm.job.status;
        delete inputForm.job.department;
        delete inputForm.job.__typename;
      }
      if (Array.isArray(inputForm.jobs)) {
        for (let i = 0; i < inputForm.jobs.length; i++) {
          delete inputForm.jobs[i].customFields;
          delete inputForm.jobs[i].location;
          delete inputForm.jobs[i].createdAt;
          delete inputForm.jobs[i].status;
          delete inputForm.jobs[i].department;
          delete inputForm.jobs[i].__typename;
        }
      }
      if (inputForm.contentShare) {
        delete inputForm.contentShare.createdAt;
        delete inputForm.contentShare.status;
        delete inputForm.contentShare.atsID;
        delete inputForm.contentShare.url;
        delete inputForm.contentShare.domain;
        delete inputForm.contentShare.imageURL;
        delete inputForm.contentShare.image;
        delete inputForm.contentShare.__typename;
      }
      if (Array.isArray(inputForm.contentShares)) {
        for (let y = 0; y < inputForm.contentShares.length; y++) {
          delete inputForm.contentShares[y].createdAt;
          delete inputForm.contentShares[y].status;
          delete inputForm.contentShares[y].atsID;
          delete inputForm.contentShares[y].url;
          delete inputForm.contentShares[y].domain;
          delete inputForm.contentShares[y].imageURL;
          delete inputForm.contentShares[y].image;
          delete inputForm.contentShares[y].__typename;
        }
      }
      const { job, contentShare, jobs, contentShares, ...rest } = inputForm;
      this.$emit('input', {
        emailMessage: {
          template: this.templateID,
          ...form,
          ...(job ? { job } : {}),
          ...(contentShare ? { contentShare } : {}),
          ...(Array.isArray(jobs) ? { jobs } : {}),
          ...(Array.isArray(contentShares) ? { contentShares } : {}),
          target: this.isIndividual ? 'INDIVIDUAL' : 'GROUP',
          replacedBody: this.body,
          inputs: {
            ...(Object.keys(rest).reduce((acc, key) => {
              if (rest[key] && typeof rest[key] === 'object') {
                acc[key] = JSON.stringify(rest[key]);
              } else {
                acc[key] = rest[key];
              }
              return acc;
            }, {})),
          }
        } });
    },
  }
};
</script>



// WEBPACK FOOTER //
// src/components/campaign-admin/update-email-message.vue